import React from 'react';

export function Figure({ label, value, note, tone, unknown }: { label: string; value: React.ReactNode; note?: React.ReactNode; tone?: 'good' | 'warn' | 'bad'; unknown?: string }) {
  return (
    <div className={`fig ${tone ?? ''}`}>
      <div className="k">{label}</div>
      {unknown ? <div className="v unknown">Not knowable yet</div> : <div className="v num">{value}</div>}
      {(note || unknown) && <div className="n">{unknown ?? note}</div>}
    </div>
  );
}

export function Meter({ label, value, threshold = [0.9, 0.97] }: { label: string; value: number | string | null; threshold?: [number, number] }) {
  const v = value === null ? null : Number(value);
  const tone = v === null ? '' : v < threshold[0] ? 'bad' : v < threshold[1] ? 'warn' : '';
  return (
    <div className={`meter ${tone}`}>
      <span>{label}</span>
      <span className="bar"><i style={{ width: `${(v ?? 0) * 100}%` }} /></span>
      <span className="num r">{v === null ? '—' : `${(v * 100).toFixed(1)}%`}</span>
    </div>
  );
}

export const Badge = ({ kind, children }: { kind: string; children: React.ReactNode }) => <span className={`badge ${kind}`}>{children}</span>;

export function Empty({ title, children }: { title: string; children?: React.ReactNode }) { return <div className="empty"><b>{title}</b>{children}</div>; }

export function Panel({ title, action, children }: { title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
  return <section className="panel"><header><h2>{title}</h2>{action}</header>{children}</section>;
}

// ---------------------------------------------------------------- loading, updating, errors
// Every read on a screen goes through useAsync. When the filters change, the figures of the old selection are never left
// on screen under the new one: `data` is null until the new answer arrives (the page shows its skeleton) and the shell
// shows "Updating…" while any read is on its way. A read that fails says so with a Retry — never a skeleton for ever.
let inflight = 0; const watchers = new Set<() => void>();
const bump = (d: number) => { inflight = Math.max(0, inflight + d); watchers.forEach(f => f()); };
const watch = (f: () => void) => { watchers.add(f); return () => { watchers.delete(f); }; };
/** True while any screen read is on its way (after a short delay, so a quick answer does not flash). */
export function useUpdating(delay = 250): boolean {
  const busy = React.useSyncExternalStore(watch, () => inflight > 0, () => false); const [shown, setShown] = React.useState(false);
  React.useEffect(() => { if (!busy) { setShown(false); return; } const t = setTimeout(() => setShown(true), delay); return () => clearTimeout(t); }, [busy, delay]);
  return shown;
}
const same = (a: readonly unknown[], b: readonly unknown[]) => a.length === b.length && a.every((x, i) => Object.is(x, b[i]));

export interface Async<T> {
  /** The answer for the CURRENT inputs only (null while the answer for new filters is on its way). */
  data: T | null; error: string | null; reload: () => void;
  /** A read is on its way. */
  loading: boolean;
  /** The inputs changed and the new answer is not in yet (an earlier answer existed). */
  updating: boolean;
}
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): Async<T> {
  const [st, setSt] = React.useState<{ for: unknown[] | null; data: T | null; error: string | null }>({ for: null, data: null, error: null });
  const [n, setN] = React.useState(0); const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let on = true; setLoading(true); bump(1); let done = false; const finish = () => { if (!done) { done = true; bump(-1); } };
    fn().then(d => { if (on) setSt({ for: deps, data: d, error: null }); }).catch(e => { if (on) setSt({ for: deps, data: null, error: String(e?.message ?? e) }); }).finally(() => { finish(); if (on) setLoading(false); });
    return () => { on = false; finish(); };
  }, [...deps, n]);   // eslint-disable-line react-hooks/exhaustive-deps
  const current = st.for != null && same(st.for, deps);
  return { data: current ? st.data : null, error: current ? st.error : null, loading: loading || !current, updating: !current && st.for != null, reload: () => { setSt(s => ({ ...s, error: null, for: s.error ? null : s.for })); setN(x => x + 1); } };
}

/** A read that failed: what went wrong in plain words, and Retry (not offered for a refusal: retrying will not change a role). */
export function ErrorBox({ error, onRetry, what }: { error: string; onRetry?: () => void; what?: string }) {
  const refused = /^Not available/.test(error) || /requires |your role|permission/i.test(error);
  return <div className="callout errbox" role="alert"><b>{what ? `${what} could not be loaded.` : 'This could not be loaded.'}</b> {error}{onRetry && !refused && <> <button type="button" className="btn" onClick={onRetry}>Retry</button></>}</div>;
}
/** While a read is on its way: the skeleton; when it failed: the error and Retry. Pass several reads to wait for all. */
export function Wait({ q, lines = 6, what }: { q: Async<unknown> | Async<unknown>[]; lines?: number; what?: string }) {
  const list = Array.isArray(q) ? q : [q]; const bad = list.find(x => x.error);
  if (bad) return <ErrorBox error={bad.error!} what={what} onRetry={() => list.forEach(x => { if (x.error) x.reload(); })} />;
  return <div className="skel" aria-busy="true" aria-label="Updating">{Array.from({ length: lines }, (_, i) => <i key={i} />)}</div>;
}
